// APFormPage — useAPForm + APFormShell pre-wired for the cookie-cutter
// route-driven create/edit page (/x/create, /x/edit/:id). Mode and id are
// inferred from the route; back / cancel / save-success navigate to
// `listRoute` (override the saved destination with `onSavedNavigate`).
// Fields read the form via `useAPFormPage()` / RHF's `useFormContext()`.
// Custom layout, nav, or non-page contexts → compose useAPForm + APFormShell
// (or APFormDialog) directly.
import { createContext, ReactNode, useContext } from 'react';
import type { DefaultValues, FieldValues } from 'react-hook-form';
import { NavigateFunction, useNavigate, useParams } from 'react-router';

import { useAPForm, type UseAPFormProps, type UseAPFormReturn } from '@/lib/form';
import { APFormShell } from './APFormShell';

export type APFormMode = 'create' | 'edit';

export interface APFormPageContextValue<TVariables extends FieldValues = FieldValues> {
  mode: APFormMode;
  baseForm: UseAPFormReturn<TVariables>;
}

const APFormPageContext = createContext<APFormPageContextValue | null>(null);

/** The enclosing APFormPage's mode + useAPForm instance (for field components). */
export function useAPFormPage<TVariables extends FieldValues = FieldValues>() {
  const ctx = useContext(APFormPageContext);
  if (!ctx) {
    throw new Error('useAPFormPage must be used inside <APFormPage>');
  }
  return ctx as unknown as APFormPageContextValue<TVariables>;
}

/** Same context, non-throwing (null when there is no <APFormPage> above). For
 *  field clusters that ALSO mount on a bare useAPForm host (APFormDialog) and
 *  take mode/baseForm as props there — the hook still runs unconditionally. */
export function useAPFormPageOptional<TVariables extends FieldValues = FieldValues>() {
  const ctx = useContext(APFormPageContext);
  return (ctx as unknown as APFormPageContextValue<TVariables> | null) ?? null;
}

export interface APFormPageProps<TVariables extends FieldValues = FieldValues>
  extends Pick<
    UseAPFormProps<TVariables>,
    | 'resourceKey'
    | 'processInitialValues'
    | 'processFormBeforeSubmit'
    | 'successCallback'
    | 'submitFields'
    | 'alsoSubmit'
    | 'refineCoreProps'
    | 'rhfProps'
  > {
  resource: string;
  /** List route — back, cancel, and (by default) save-success navigate here. */
  listRoute: string;
  /** Already-translated title; function form receives the inferred mode. */
  title: ReactNode | ((mode: APFormMode) => ReactNode);
  /** Already-translated supporting copy; function form receives the inferred mode. */
  description?: ReactNode | ((mode: APFormMode) => ReactNode);
  /** Function form keeps the create-only ternary pattern mode-safe. */
  defaultValues?:
    | DefaultValues<TVariables>
    | ((mode: APFormMode) => DefaultValues<TVariables> | undefined);
  /** Save-success navigation override (e.g. groups → the saved record's show page). */
  onSavedNavigate?: (result: any, navigate: NavigateFunction) => void;
  /** Field components (inside the RHF provider — `useAPFormPage()` for form access). */
  children: ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  /** Drop the single card body so children compose their own APFormSection
   *  cards on the canvas (deliver-style sectioned layout). */
  sectioned?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function APFormPage<TVariables extends FieldValues = FieldValues>({
  resource,
  listRoute,
  title,
  description,
  resourceKey,
  defaultValues,
  processInitialValues,
  processFormBeforeSubmit,
  successCallback,
  submitFields,
  alsoSubmit,
  refineCoreProps,
  rhfProps,
  onSavedNavigate,
  children,
  submitLabel,
  cancelLabel,
  sectioned,
  className,
  'data-testid': testId,
}: APFormPageProps<TVariables>) {
  const navigate = useNavigate();
  const { id } = useParams();
  const mode: APFormMode = id !== undefined ? 'edit' : 'create';

  const baseForm = useAPForm<TVariables>({
    resource,
    ...(resourceKey !== undefined ? { resourceKey } : {}),
    mode,
    id,
    defaultValues: typeof defaultValues === 'function' ? defaultValues(mode) : defaultValues,
    processInitialValues,
    processFormBeforeSubmit,
    successCallback,
    ...(submitFields !== undefined ? { submitFields } : {}),
    ...(alsoSubmit !== undefined ? { alsoSubmit } : {}),
    keepFormOpenAfterSave: true, // the page owns navigation (onBack / onSavedNavigate)
    refineCoreProps,
    rhfProps,
  });

  // Not memoized: `baseForm` (and its submit/cancel closures) is per-render by
  // design, and the children subtree re-renders with this component anyway.
  const contextValue: APFormPageContextValue = { mode, baseForm: baseForm as UseAPFormReturn };

  return (
    <APFormPageContext.Provider value={contextValue}>
      <APFormShell<TVariables>
        baseForm={baseForm}
        title={typeof title === 'function' ? title(mode) : title}
        description={typeof description === 'function' ? description(mode) : description}
        onBack={() => navigate(listRoute)}
        onSaved={onSavedNavigate ? (result) => onSavedNavigate(result, navigate) : undefined}
        submitLabel={submitLabel}
        cancelLabel={cancelLabel}
        {...(sectioned !== undefined ? { sectioned } : {})}
        className={className}
        data-testid={testId}
      >
        {children}
      </APFormShell>
    </APFormPageContext.Provider>
  );
}
